import { z } from 'zod';
import type { ShippingTypeToken } from '@sleed/bizcommerce-api';

/**
 * Shipping type (Τρόπος Αποστολής) edit form. Mirrors `ShippingTypeToken` and the legacy admin popup
 * (Core/client/admin/src/pages/Orders/ShippingTypes/index.js): nm required ≤100, cd ≤50, cid/rid FKs,
 * delivery flags (stw/ctd/crd/isTransportCourier/isDefault), constraints (frt/tprc/amcprc/min-max
 * weight + max length/width/height/vol-weight + maxWeightPerProduct), erpcd/erpcd2, trackingUrlTemplate ≤2000
 * (Handlebars, validated server-side), description HTML, img1.
 * `nm` is multilanguage server-side (writes go to the active Accept-Language, default `el`). The
 * `cglst` (charges), `avail_payments` (payment compatibility) and postal-code map relations are managed
 * separately. `shippingFee`/`paymentFee`/`pstid` are preserved as-is (not surfaced in the UI).
 */
const nullableStr = z.string().nullable();
const nullableNumber = z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().nullable());

export const shippingTypeSchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	cd: z.string().trim().max(50, 'validation.tooLong').nullable(),
	erpcd: nullableStr,
	erpcd2: nullableStr,
	trackingUrlTemplate: z.string().trim().max(2000, 'validation.tooLong').nullable(),
	cid: z.number().nullable(),
	cids: z.array(z.number()),
	rid: z.number().nullable(),
	rids: z.array(z.number()),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	isDefault: z.boolean(),
	amcprc: nullableNumber,
	frt: nullableNumber,
	tprc: nullableNumber,
	minw: nullableNumber,
	maxw: nullableNumber,
	maxLength: nullableNumber,
	maxWidth: nullableNumber,
	maxHeight: nullableNumber,
	maxVolWeight: nullableNumber,
	maxWeightPerProduct: z.boolean(),
	stw: z.boolean(),
	ctd: z.boolean(),
	crd: z.boolean(),
	isTransportCourier: z.boolean(),
	description: nullableStr,
	CustomerGroupIds: z.array(z.number()).nullable(),
	ExcludedCustomerGroupIds: z.array(z.number()).nullable(),
	// Preserved passthrough (not edited in this surface).
	shippingFee: nullableNumber,
	paymentFee: nullableNumber,
	pstid: z.number().nullable(),
	img1: nullableStr,
	imgdel1: z.boolean(),
	img1File: z.instanceof(File).nullable()
});

export type ShippingTypeFormValues = z.infer<typeof shippingTypeSchema>;

export const EMPTY_SHIPPING_TYPE: ShippingTypeFormValues = {
	nm: '',
	cd: null,
	erpcd: null,
	erpcd2: null,
	trackingUrlTemplate: null,
	cid: null,
	cids: [],
	rid: null,
	rids: [],
	iv: true,
	si: 0,
	isDefault: false,
	amcprc: null,
	frt: null,
	tprc: null,
	minw: null,
	maxw: null,
	maxLength: null,
	maxWidth: null,
	maxHeight: null,
	maxVolWeight: null,
	maxWeightPerProduct: false,
	stw: false,
	ctd: false,
	crd: false,
	isTransportCourier: false,
	description: null,
	CustomerGroupIds: [],
	ExcludedCustomerGroupIds: [],
	shippingFee: null,
	paymentFee: null,
	pstid: null,
	img1: null,
	imgdel1: false,
	img1File: null
};

export function tokenToForm(token: ShippingTypeToken): ShippingTypeFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		cd: token.cd ?? null,
		erpcd: token.erpcd ?? null,
		erpcd2: token.erpcd2 ?? null,
		trackingUrlTemplate: token.trackingUrlTemplate ?? null,
		cid: token.cid ?? null,
		// Auto-adopt a legacy single country when the multi-set is empty (parity with the grid + backend read).
		cids: token.cids && token.cids.length > 0 ? token.cids : token.cid != null ? [token.cid] : [],
		rid: token.rid ?? null,
		// Auto-adopt a legacy single region when the multi-set is empty (parity with the grid + backend read).
		rids: token.rids && token.rids.length > 0 ? token.rids : token.rid != null ? [token.rid] : [],
		iv: token.iv ?? false,
		si: token.si ?? 0,
		isDefault: token.isDefault ?? false,
		amcprc: token.amcprc ?? null,
		frt: token.frt ?? null,
		tprc: token.tprc ?? null,
		minw: token.minw ?? null,
		maxw: token.maxw ?? null,
		maxLength: token.maxLength ?? null,
		maxWidth: token.maxWidth ?? null,
		maxHeight: token.maxHeight ?? null,
		maxVolWeight: token.maxVolWeight ?? null,
		maxWeightPerProduct: token.maxWeightPerProduct ?? false,
		stw: token.stw ?? false,
		ctd: token.ctd ?? false,
		crd: token.crd ?? false,
		isTransportCourier: token.isTransportCourier ?? false,
		description: token.description ?? null,
		CustomerGroupIds: token.CustomerGroupIds ?? [],
		ExcludedCustomerGroupIds: token.ExcludedCustomerGroupIds ?? [],
		shippingFee: token.shippingFee ?? null,
		paymentFee: token.paymentFee ?? null,
		pstid: token.pstid ?? null,
		img1: token.img1 ?? null,
		imgdel1: false,
		img1File: null
	};
}

export function formToToken(form: ShippingTypeFormValues): ShippingTypeToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		cd: form.cd?.trim() || null,
		erpcd: form.erpcd,
		erpcd2: form.erpcd2,
		trackingUrlTemplate: form.trackingUrlTemplate?.trim() || null,
		// Keep the legacy scalars consistent with the multi-sets: null when cleared, so the backend does not
		// resurrect a stale cid/rid (its empty-set back-compat backfill only kicks in for true legacy callers).
		cid: form.cids.length > 0 ? form.cids[0] : null,
		cids: form.cids,
		rid: form.rids.length > 0 ? form.rids[0] : null,
		rids: form.rids,
		iv: form.iv,
		si: form.si,
		isDefault: form.isDefault,
		amcprc: form.amcprc,
		frt: form.frt,
		tprc: form.tprc,
		minw: form.minw,
		maxw: form.maxw,
		maxLength: form.maxLength,
		maxWidth: form.maxWidth,
		maxHeight: form.maxHeight,
		maxVolWeight: form.maxVolWeight,
		maxWeightPerProduct: form.maxWeightPerProduct,
		stw: form.stw,
		ctd: form.ctd,
		crd: form.crd,
		isTransportCourier: form.isTransportCourier,
		description: form.description,
		CustomerGroupIds: form.CustomerGroupIds,
		ExcludedCustomerGroupIds: form.ExcludedCustomerGroupIds,
		shippingFee: form.shippingFee,
		paymentFee: form.paymentFee,
		pstid: form.pstid,
		img1: form.img1,
		imgdel1: form.imgdel1
	};
}
